import { useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { cancelTokenSource } from '../api/axios';
import {
  fetchVideoFeedVideosAndType,
  getUserOrganization,
  postCompleteMultiPartUploadChunks,
  postCreateMultiPartUpload,
  postRegeneratePresignedUrls,
  postUploadChunkByPresignedUrl,
  postUploadMetaData,
} from '../api';
import { VIDEO_TYPE } from '../enums';
import useWorkerDelay from './useWorkerDelay';
import {
  CompletedMultipartPart,
  getLocalCompletedPartsForUpload,
  getMissingPartNumbers,
  getUploadedBytesForStoredUpload,
  hasEveryPart,
  normalizeCompletedParts,
  runWithConcurrency,
} from './multipartUpload';

const MULTIPART_PART_SIZE = 5 * 1024 * 1024;
const MAX_CONCURRENT_PARTS = 3;
type ActiveAwsUpload = { controller: AbortController; settled: Promise<void> };
const activeAwsUploads = new Set<ActiveAwsUpload>();
let awsUploadCancellationRequested = false;

export const isAwsUploadCancellationRequested = () => awsUploadCancellationRequested;

export const abortActiveAwsUploads = async () => {
  awsUploadCancellationRequested = true;
  const uploads = Array.from(activeAwsUploads);
  uploads.forEach(({ controller }) => controller.abort());
  await Promise.all(uploads.map(({ settled }) => settled));
};

const showPersistentUploadWarning = (message: string) => {
  enqueueSnackbar(message, {
    preventDuplicate: true,
    variant: 'warning',
    persist: true,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
  });
};

const fetchUserOrganization = async () => {
  try {
    const { data: userOrganization } = await getUserOrganization();
    if (+userOrganization?.hours_limit_used >= +userOrganization?.hours_limit) {
      showPersistentUploadWarning('Hours limit reached');
      return;
    }
  } catch (err) {
    console.log(err);
  }
};

function getVideofeedTitle(input: unknown): string | undefined {
  const seen = new WeakSet<object>();

  const dfs = (node: unknown): string | undefined => {
    if (!node || typeof node !== 'object') return;
    if (seen.has(node as object)) return;
    seen.add(node as object);

    const obj = node as Record<string, unknown>;
    if (typeof obj.videofeedTitle === 'string') return obj.videofeedTitle;

    for (const key in obj) {
      const val = obj[key];
      const found = dfs(val);
      if (found !== undefined) return found;
    }
  };

  return dfs(input);
}

function hasUploadedMetadataResumeState(resumeData: any): boolean {
  return Boolean(resumeData && typeof resumeData === 'object' && resumeData.metadataUploaded === true);
}

function markMetadataUploaded(keyStorage: string) {
  const raw = localStorage.getItem(keyStorage);
  if (!raw) {
    return;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return;
    }
    parsed.metadataUploaded = true;
    parsed.uploadedBytes = getUploadedBytesForStoredUpload(
      Number(parsed.size),
      MULTIPART_PART_SIZE,
      parsed.videos,
      true,
    );
    if (Number(parsed.size) > 0) {
      parsed.overallProgress = Math.min(100, Math.ceil((Number(parsed.uploadedBytes) / Number(parsed.size)) * 100));
    }
    localStorage.setItem(keyStorage, JSON.stringify(parsed));
  } catch {}
}

function applyPresignedUrlsFromPart(presignedUrls: string[], startPart: number, urls: string[]) {
  urls.forEach((url, index) => {
    presignedUrls[startPart - 1 + index] = url;
  });
}

function getStoredUpload(keyStorage: string): any | null {
  try {
    const raw = localStorage.getItem(keyStorage);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const useAwsUploader = () => {
  const [localStorageKey, setLocalStorageKey] = useState('');
  const [trigger, setTrigger] = useState(0);
  const uploadControllerRef = useRef<AbortController | null>(null);
  const { delay } = useWorkerDelay();

  useEffect(
    () => () => {
      uploadControllerRef.current?.abort();
    },
    [],
  );

  const upload = async (data: { file: File | Blob; itsZip?: boolean; video_id?: string; videofeedId: string; keyStorage: string }) => {
    const { itsZip, video_id, file, videofeedId, keyStorage } = data;
    if (!videofeedId) {
      return false;
    }

    setLocalStorageKey(keyStorage);
    if (awsUploadCancellationRequested) {
      throw new DOMException('Upload aborted', 'AbortError');
    }

    const uploadController = new AbortController();
    let settleUpload = () => {};
    const activeUpload: ActiveAwsUpload = {
      controller: uploadController,
      settled: new Promise<void>((resolve) => {
        settleUpload = resolve;
      }),
    };
    const throwIfUploadAborted = () => {
      if (uploadController.signal.aborted || awsUploadCancellationRequested) {
        throw new DOMException('Upload aborted', 'AbortError');
      }
    };
    const waitForRetry = (ms: number) => {
      throwIfUploadAborted();
      return new Promise<void>((resolve, reject) => {
        const onAbort = () => reject(new DOMException('Upload aborted', 'AbortError'));
        uploadController.signal.addEventListener('abort', onAbort, { once: true });
        delay(ms).then(() => {
          uploadController.signal.removeEventListener('abort', onAbort);
          resolve();
        });
      });
    };

    uploadControllerRef.current = uploadController;
    activeAwsUploads.add(activeUpload);

    try {
      if (!itsZip && video_id) {
        const numParts = Math.ceil(file.size / MULTIPART_PART_SIZE);
        const storedUpload = getStoredUpload(keyStorage);
        const storedVideo = storedUpload?.videos?.find((video: VIDEO_TYPE) => String(video.id) === String(video_id));
        const localUploadId = String(storedVideo?.uploadId || '');
        let serverUploadId = '';
        let serverVideoUploaded = false;

        try {
          const res = await fetchVideoFeedVideosAndType(videofeedId);

          const serverVideo = res.data.videos?.find((video: VIDEO_TYPE) => String(video.id) === String(video_id));
          serverVideoUploaded = serverVideo?.status === 'uploaded';
          serverUploadId = String(serverVideo?.upload_id || '');
        } catch (err) {
          console.log(err);
        }
        throwIfUploadAborted();

        let uploadId = serverUploadId || localUploadId;
        let completedParts = getLocalCompletedPartsForUpload(uploadId, localUploadId, storedVideo?.chunks);
        let presignedUrls: string[] = [];

        const persistVideoState = (parts: CompletedMultipartPart[], uploaded = false) => {
          const json = getStoredUpload(keyStorage);
          if (!json?.videos) return;

          const videoIndex = json.videos.findIndex((video: VIDEO_TYPE) => String(video.id) === String(video_id));
          if (videoIndex === -1) return;

          const normalizedParts = normalizeCompletedParts(parts).filter((part) => part.PartNumber <= numParts);
          const nextMissingPart = getMissingPartNumbers(numParts, normalizedParts)[0];
          json.videos[videoIndex].uploadId = uploadId;
          json.videos[videoIndex].chunks = normalizedParts;
          json.videos[videoIndex].partNumber = nextMissingPart ? nextMissingPart - 1 : numParts;
          json.videos[videoIndex].status = uploaded ? 'uploaded' : 'in_progress';
          delete json.videos[videoIndex].presignedUrls;

          json.uploadedBytes = getUploadedBytesForStoredUpload(
            Number(json.size),
            MULTIPART_PART_SIZE,
            json.videos,
            json.metadataUploaded === true,
          );
          if (Number(json.size) > 0) {
            json.overallProgress = Math.min(100, Math.ceil((Number(json.uploadedBytes) / Number(json.size)) * 100));
          }

          try {
            localStorage.setItem(keyStorage, JSON.stringify(json));
          } catch (error) {
            console.warn('Failed to persist upload progress', error);
          }
          setTrigger((current) => current + 1);
        };

        if (serverVideoUploaded) {
          persistVideoState(completedParts, true);
          return true;
        }

        if (!uploadId) {
          const createMultiPartData = await postCreateMultiPartUpload(video_id, videofeedId, numParts);
          throwIfUploadAborted();
          uploadId = String(createMultiPartData.data?.uploadId || '');
          presignedUrls = Array.isArray(createMultiPartData.data?.presignedUrls) ? [...createMultiPartData.data.presignedUrls] : [];
          completedParts = [];
        }

        if (!uploadId) {
          throw new Error('Failed to start multipart upload');
        }

        persistVideoState(completedParts);

        const maxAttempts = 10;
        const retryDelay = 5000;
        const completedPartsByNumber = new Map(completedParts.map((part) => [part.PartNumber, part]));
        const missingPartNumbers = getMissingPartNumbers(numParts, completedParts);
        let presignedRefreshPromise: Promise<void> | null = null;

        const isUploadAborted = (error?: unknown) =>
          uploadController.signal.aborted || (error as { name?: string })?.name === 'AbortError';

        const refreshPresignedUrls = async () => {
          if (!presignedRefreshPromise) {
            const firstMissingPart = missingPartNumbers[0] || 1;
            presignedRefreshPromise = postRegeneratePresignedUrls(uploadId, firstMissingPart, numParts)
              .then((regenerated) => {
                applyPresignedUrlsFromPart(presignedUrls, firstMissingPart, regenerated.data || []);
              })
              .finally(() => {
                presignedRefreshPromise = null;
              });
          }
          await presignedRefreshPromise;
          throwIfUploadAborted();
        };

        const uploadPart = async (partNumber: number) => {
          if (partNumber % 10 === 0) {
            await fetchUserOrganization();
            throwIfUploadAborted();
          }

          const start = (partNumber - 1) * MULTIPART_PART_SIZE;
          const end = Math.min(start + MULTIPART_PART_SIZE, file.size);
          const filePart = file.slice(start, end);

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              if (uploadController.signal.aborted) throw new DOMException('Upload aborted', 'AbortError');
              if (!presignedUrls[partNumber - 1]) await refreshPresignedUrls();

              const presignedUrl = presignedUrls[partNumber - 1];
              if (!presignedUrl) throw new Error(`Missing upload URL for part ${partNumber}`);

              const response = await postUploadChunkByPresignedUrl(filePart, presignedUrl, uploadController.signal);
              completedPartsByNumber.set(partNumber, { PartNumber: partNumber, ETag: response.ETag });
              persistVideoState(Array.from(completedPartsByNumber.values()));
              return;
            } catch (error) {
              if (isUploadAborted(error)) throw error;
              console.error(`Failed to upload part ${partNumber}, attempt ${attempt}`, error);
              if (attempt === maxAttempts) throw error;

              try {
                await refreshPresignedUrls();
              } catch (refreshError) {
                if (isUploadAborted(refreshError)) throw refreshError;
                console.error('Failed to regenerate presigned URLs', refreshError);
              }
              await waitForRetry(retryDelay);
            }
          }
        };

        let success = false;
        await runWithConcurrency(missingPartNumbers, MAX_CONCURRENT_PARTS, uploadPart, () => uploadController.abort());
        throwIfUploadAborted();

        const finalParts = normalizeCompletedParts(Array.from(completedPartsByNumber.values())).filter(
          (part) => part.PartNumber <= numParts,
        );
        if (!hasEveryPart(numParts, finalParts)) {
          throw new Error('Multipart upload is missing one or more parts');
        }

        for (let attempt = 1; attempt <= 10; attempt++) {
          try {
            await postCompleteMultiPartUploadChunks(uploadId, finalParts);
            throwIfUploadAborted();
            persistVideoState(finalParts, true);
            success = true;
            break;
          } catch (error: any) {
            if (isUploadAborted(error)) throw error;
            console.log(`Attempt ${attempt} failed.`);
            if (error?.response) {
              const message = error.response.data?.message || error.response.data?.error || 'Upload failed';
              if (error.response.status < 500 || /limit|storage/i.test(message)) {
                showPersistentUploadWarning(message);
                return false;
              }
            }

            if (attempt < 10) {
              console.log('Retrying postCompleteMultiPartUploadChunks in 5 second...');
              await waitForRetry(5000);
            } else {
              console.log('Max retries reached. Fetching fallback data...');
            }
          }
        }

        throwIfUploadAborted();
        const { data } = await getUserOrganization();
        throwIfUploadAborted();
        if (data) {
          if (+data?.hours_limit_used >= +data?.hours_limit) {
            showPersistentUploadWarning('Hours limit reached');
            return false;
          }
        }

        return success;
      }

      if (itsZip) {
        let metadataAlreadyUploaded = false;
        try {
          const raw = localStorage.getItem(keyStorage);
          metadataAlreadyUploaded = Boolean(raw && hasUploadedMetadataResumeState(JSON.parse(raw)));
        } catch {}
        if (metadataAlreadyUploaded) {
          throwIfUploadAborted();
          markMetadataUploaded(keyStorage);
          setTrigger((current) => current + 1);
          return true;
        }

        const maxAttempts = 10;
        const retryDelay = 5000;
        let res: any;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            res = await postUploadMetaData(file, videofeedId);
            throwIfUploadAborted();
            markMetadataUploaded(keyStorage);
            setTrigger((current) => current + 1);
            break;
          } catch (err: any) {
            if (uploadController.signal.aborted || err?.name === 'AbortError') throw err;
            console.log(`Attempt ${attempt} failed:`, err);

            // If server responded, DON'T retry — surface its message.
            if (err?.response) {
              const { status, data } = err.response;
              const msg = (data && (data.message ?? data.error ?? data.detail)) ?? (typeof data === 'string' ? data : 'Unknown server error');
              const title = getVideofeedTitle(err);
              const now = new Date();
              const formattedTime =
                `${now.getDate()} ${now.toLocaleString('en', { month: 'short' })} ` +
                `${String(now.getHours()).padStart(2, '0')}:` +
                `${String(now.getMinutes()).padStart(2, '0')}:` +
                `${String(now.getSeconds()).padStart(2, '0')}`;
              enqueueSnackbar(`${title ? `(${title})` : ''} Server error: An error occurred while parsing plist data. ${formattedTime}`, {
                variant: 'error',
                anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                persist: true,
              });
              localStorage.removeItem(keyStorage);
              throw new Error(`Server error ${status}: ${msg}`);
            }

            // Only retry on real network loss/timeout
            const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
            const timeout = err?.code === 'ECONNABORTED' || err?.name === 'AbortError';
            const networkLoss = offline || timeout || !err?.response;

            if (attempt === maxAttempts || !networkLoss) {
              throw new Error(err?.message || `Failed after ${maxAttempts} attempts`);
            }

            console.log(`Retrying in ${retryDelay / 1000} seconds...`);
            await waitForRetry(retryDelay);
          }
        }

        return res;
      }
      return false;
    } finally {
      activeAwsUploads.delete(activeUpload);
      if (uploadControllerRef.current === uploadController) {
        uploadControllerRef.current = null;
      }
      settleUpload();
    }
  };

  const cancelUploading = () => {
    void abortActiveAwsUploads();
    cancelTokenSource.cancel();
    window.location.reload();
  };

  return {
    upload,
    cancelUploading,
    localStorageKey,
    trigger,
  };
};

export default useAwsUploader;
